import { FC, useRef } from 'react';
import { List, Skeleton } from 'antd';
import { useQuery } from '@apollo/client';
import { GET_PATIENT_APPOINTMENTS } from 'gql/appointments/queries';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Appointments as AppointmentsType } from 'gql/appointments/__generated__/Appointments';
import { StyledListItemContent } from 'theme/styled';
import { setTour } from 'store/tour/slice';
import { useAppDispatch } from 'store/hooks';

import { showErrorMessage } from 'utils/showErrorMessage';
import AppointmentItem from './AppointmentItem';

const PAGE_LIMIT = 10;

interface IProps {
  id: string;
  setTotal(total: number): void;
}

const Appointments: FC<IProps> = ({ id, setTotal }) => {
  const page = useRef(0);
  const dispatch = useAppDispatch();

  const { data, loading, fetchMore } = useQuery<AppointmentsType>(
    GET_PATIENT_APPOINTMENTS,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables: {
        query: {
          name: '',
          plan_type: '',
          patient: id,
          pagination: {
            page: 0,
            limit: PAGE_LIMIT
          }
        }
      },
      onCompleted(data) {
        setTotal(data?.appointmentsForDoctor?.data?.total || 0);
        dispatch(setTour({ key: 'patients', value: true }));
      },
      onError: err => showErrorMessage(err)
    }
  );

  const loadMoreData = () => {
    page.current = page.current + 1;

    fetchMore({
      variables: {
        query: {
          name: '',
          plan_type: '',
          patient: id,
          pagination: {
            page: page.current,
            limit: PAGE_LIMIT
          }
        }
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const newResult =
          fetchMoreResult.appointmentsForDoctor?.data.results || [];

        if (!previousResult.appointmentsForDoctor?.data.results || !newResult) {
          return previousResult;
        }

        return {
          appointmentsForDoctor: {
            ...previousResult.appointmentsForDoctor,
            data: {
              ...previousResult.appointmentsForDoctor.data,
              results: [
                ...previousResult.appointmentsForDoctor.data.results,
                ...newResult
              ]
            }
          }
        };
      }
    });
  };

  const results = data?.appointmentsForDoctor?.data.results || [];
  const total = data?.appointmentsForDoctor?.data.total || 0;

  return (
    <StyledListItemContent id="appointments-list">
      <InfiniteScroll
        dataLength={results.length}
        next={loadMoreData}
        hasMore={results.length < total}
        loader={
          <div className="list-content-item" style={{ marginTop: 16 }}>
            <Skeleton title={false} paragraph={{ rows: 2 }} active />
          </div>
        }
        scrollableTarget="appointments-list"
      >
        <List
          loading={loading}
          dataSource={results}
          renderItem={item => (
            <List.Item className="list-content-item">
              <AppointmentItem
                id={item.id}
                dates={item.dates}
                plan={item.plan}
                paid={item.paid}
                status={item.status}
              />
            </List.Item>
          )}
        />
      </InfiniteScroll>
    </StyledListItemContent>
  );
};

export default Appointments;
